import { AttachToModelFacet, LinkToModelFacet, MovementConstraintFacet, convertToNumber16, worldToLocalTransform } from "flavr-common";
import { Matrix4 } from "flavr-math";
import { modelPool } from "@modules/model";
function cloneAttachment(facet) {
    return new AttachToModelFacet(facet.targetUid, Object.assign(Object.assign({}, facet.params), { offsetMatrix: facet.params.offsetMatrix === undefined ? undefined : convertToNumber16(new Matrix4().fromArray(facet.params.offsetMatrix)) }));
}
function cloneLink(facet) {
    return new LinkToModelFacet(facet.targetUID, facet.offsetMatrix === undefined ? undefined : convertToNumber16(new Matrix4().fromArray(facet.offsetMatrix)));
}
function cloneMovementConstraint(facet) {
    return new MovementConstraintFacet(facet.enabled, facet.constraints.map(constraint => (Object.assign({}, constraint))));
}
export class TrainingSceneResetService {
    constructor() {
        this.initialTransforms = new Map();
    }
    capture(modelUids) {
        this.initialTransforms.clear();
        for (const uid of modelUids) {
            const root = modelPool.getModel(uid);
            if (root === undefined)
                throw new Error(`[training] Cannot capture missing model ${uid}`);
            root.traverse(model => {
                var _a;
                if (this.initialTransforms.has(model.getUID()))
                    return;
                const attachment = model.getFacet(AttachToModelFacet);
                const link = model.getFacet(LinkToModelFacet);
                const movementConstraint = model.getFacet(MovementConstraintFacet);
                this.initialTransforms.set(model.getUID(), {
                    parentUid: (_a = model.getParent()) === null || _a === void 0 ? void 0 : _a.getUID(),
                    localTransform: model.getTransform().clone(),
                    worldTransform: model.getWorldTransform().clone(),
                    attachment: attachment === undefined ? undefined : cloneAttachment(attachment),
                    link: link === undefined ? undefined : cloneLink(link),
                    movementConstraint: movementConstraint === undefined ? undefined : cloneMovementConstraint(movementConstraint)
                });
            });
        }
    }
    getModelUids() {
        return [...this.initialTransforms.keys()];
    }
    getInitialAttachmentOffset(childUid, parentUid) {
        const child = this.initialTransforms.get(childUid);
        const parent = this.initialTransforms.get(parentUid);
        if (child === undefined || parent === undefined) {
            throw new Error(`[training] Missing initial attachment transform for ${childUid} relative to ${parentUid}`);
        }
        return worldToLocalTransform(child.worldTransform, parent.worldTransform, new Matrix4());
    }
    getModel(uid) {
        const model = modelPool.getModel(uid);
        if (model === undefined)
            throw new Error(`[training] Cannot restore missing model ${uid}`);
        return model;
    }
    getInitialDepth(uid) {
        var _a, _b;
        let depth = 0;
        let parentUid = (_a = this.initialTransforms.get(uid)) === null || _a === void 0 ? void 0 : _a.parentUid;
        while (parentUid !== undefined && this.initialTransforms.has(parentUid)) {
            depth++;
            parentUid = (_b = this.initialTransforms.get(parentUid)) === null || _b === void 0 ? void 0 : _b.parentUid;
        }
        return depth;
    }
    restore() {
        // Remove runtime links first so moving a parent cannot drag a dropped or held object along.
        for (const uid of this.initialTransforms.keys()) {
            const model = this.getModel(uid);
            model.removeFacet(AttachToModelFacet);
            model.removeFacet(LinkToModelFacet);
            model.removeFacet(MovementConstraintFacet);
        }
        // Restore parents first, even when a child appeared before its parent in the scenario.
        const orderedTransforms = [...this.initialTransforms].sort(([left], [right]) => this.getInitialDepth(left) - this.getInitialDepth(right));
        for (const [uid, initial] of orderedTransforms) {
            const model = this.getModel(uid);
            if (initial.parentUid !== undefined) {
                const parent = modelPool.getModel(initial.parentUid);
                if (parent === undefined)
                    throw new Error(`[training] Cannot restore missing parent ${initial.parentUid} of model ${uid}`);
                model.setParent(parent);
            }
            model.setTransform(initial.localTransform);
        }
        for (const [uid, initial] of this.initialTransforms) {
            const model = this.getModel(uid);
            if (initial.attachment !== undefined) {
                model.setFacet(cloneAttachment(initial.attachment));
            }
            if (initial.link !== undefined) {
                model.setFacet(cloneLink(initial.link));
            }
        }
        // Re-enabling constraints at the restored pose clears cached NavMesh positions and anchors.
        for (const [uid, initial] of this.initialTransforms) {
            if (initial.movementConstraint !== undefined) {
                this.getModel(uid).setFacet(cloneMovementConstraint(initial.movementConstraint));
            }
        }
    }
}
